import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { FETCH_STATE } from 'constants';
import { abilityConfig } from 'config/ability';
import { UpdateVehicleGroupModal } from 'components/Modals';
import { ReservationsActions } from 'state/modules/reservations';
import VehiclePlaceholder from 'assets/img/vehicle_placeholder.png';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { useStyles, listStyles, customCardStyles } from './Details.styled';
import { Box, Button, List, Divider, ListItem, Typography, ListItemText } from '@material-ui/core';

const VehiclesGroupsDetails = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const itemsClasses = listStyles();
  const cardClasses = customCardStyles();
  const [openUpdateVehicleGroupModal, setOpenUpdateVehicleGroupModal] = useState(false);
  const { orgType } = useContext(UserContext);
  const current = useSelector((state) => state.vehiclesGroups.current);
  const rules = useSelector((state) => state.reservations.rules);
  const fetchingUpdateImage = useSelector((state) => state?.files?.fetchingUpdateImage);
  const fetchingUploadImage = useSelector((state) => state?.files?.fetchingUploadImage);
  const fetchingDeleteImage = useSelector((state) => state?.files?.fetchingDeleteImage);
  const fetchedUpdateImageSuccess = useSelector((state) => state?.files?.fetchedUpdateImageSuccess);
  const fetchedUploadImageSuccess = useSelector((state) => state?.files?.fetchedUploadImageSuccess);
  const fetchedDeleteImageSuccess = useSelector((state) => state?.files?.fetchedDeleteImageSuccess);
  const updateFleets = useSelector((state) => state.vehiclesGroups.updateFleets);

  const refetch =
    (!fetchingUpdateImage && fetchedUpdateImageSuccess) ||
    (!fetchingUploadImage && fetchedUploadImageSuccess) ||
    (!fetchingDeleteImage && fetchedDeleteImageSuccess) ||
    updateFleets === FETCH_STATE.DONE;

  useEffect(() => {
    if (id && refetch) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupById(id, null));
    }
  }, [dispatch, refetch]);

  useEffect(() => {
    if (id) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupById(id, null));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (orgType === 'msServiceOrg' && abilityConfig.can('enabled', 'reservation')) {
      dispatch(ReservationsActions.fetchReservationRules({ is_enabled: true }));
    }
  }, [orgType, dispatch]);  // eslint-disable-line

  const handleUpdateVehicleGroupModal = (e) => {
    setOpenUpdateVehicleGroupModal((prevState) => !prevState);
  };

  const getVehicleGroupsData = (vg = {}, isOwner) => {
    const vgDetailsInfo = [
      { label: `${i18n.t('common.name')}:`, value: vg?.name },
      { label: `${i18n.t('common.fleet_owner')}:`, value: vg?.parent?.organization_name },
      { label: `${i18n.t('common.fleet')}:`, value: vg?.parent?.name },
      {
        label: `${i18n.t('vehicle_groups.pricing_rule_set')}:`,
        value: vg?.current_pricing_rule_set?.name,
      },
      { label: `${i18n.t('vehicle_groups.location')}:`, value: vg?.primary_location?.name },
      { label: `${i18n.t('common.time_zone')}:`, value: vg?.time_zone },
      ...(!isOwner
        ? [
            {
              label: `${i18n.t('vehicle_groups.customer_access')}:`,
              value: vg?.driver_access ? i18n.t('common.yes') : i18n.t('common.no'),
            },
          ]
        : []),
    ];

    return vgDetailsInfo;
  };

  const vgDetails = useMemo(
    () =>
      getVehicleGroupsData(current, orgType === 'msOwnerOrg').map((val, index) =>
        !val?.value ? null : (
          <ListItem key={index} className={classes.listItem}>
            <ListItemText
              primary={val.label}
              secondary={val.value}
              classes={itemsClasses}
              data-cy={`details-${val.label}`}
            />
          </ListItem>
        )
      ),
    [current, orgType]
  );

  const getTitle = () => {
    return orgType === 'msOwnerOrg'
      ? i18n.t('vehicle_groups.service_area_information')
      : i18n.t('vehicle_groups.vehicle_group_information');
  };

  return (
    <Card
      headerActionType="vertical"
      title={getTitle()}
      data-cy="vehicle-groups-card"
      contentRootClasses={cardClasses}
    >
      <>
        <Box px={4} py={2} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" color="textPrimary">
            <Box fontSize={13} fontWeight="fontWeightMedium">
              {i18n.t('common.id')}# {id}
            </Box>
          </Typography>
          <Typography>
            <Button
              variant="text"
              color="primary"
              className={classes.button}
              onClick={handleUpdateVehicleGroupModal}
            >
              {i18n.t('vehicle_groups.update_information')}
            </Button>
          </Typography>
        </Box>
        {current?.default_file?.thumbnail_url && (
          <Box textAlign="center">
            <img
              alt={current?.name || ''}
              className={classes.vehicleGroupImg}
              src={current?.default_file?.thumbnail_url || VehiclePlaceholder}
            />
          </Box>
        )}
        <Divider />

        <Box className={classes.cardContentPadding}>
          <List className={classes.list}>{vgDetails}</List>
        </Box>
      </>
      {openUpdateVehicleGroupModal && (
        <UpdateVehicleGroupModal
          open={true}
          rules={rules}
          vehicleGroup={current}
          handleClick={handleUpdateVehicleGroupModal}
        />
      )}
    </Card>
  );
};

export default VehiclesGroupsDetails;
